import React from 'react'
import { Alert } from 'react-bootstrap'

import { ISupportSpinner } from '../../../../../../shared/Ajax'
import { getLastFY, getThisFY } from '../../../../../../shared/Utility'
import { Option } from '../../../../../../shared/fields/Options'

import { Label } from '../../../../../../shared/fields'
import { Columns } from '../../../../../../shared/EditableTable/Model'
import { ApplicationForm2Editable } from '../../../../EditableTable'

import initialDataExportMarketFinancialConfig from './ExportMarketFinancialsConfig'
import { ApplicationModel } from '../../../../../../models/ApplicationModel'
import { ApplicationController } from '../../../../../../controllers/ApplicationController'

import { ApplicationExportMarketFinancialsModel } from '../../../../../../models/ApplicationExportMarketFinancialsModel'

export interface Props {
    model: ApplicationModel
    markets: Option<string>[] // {value:string;label:string;}[]
    marketsRegions: string[]
    preview?: boolean
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    tierNo: number,
}

export interface State {
    filteredMarkets: Option<string>[],
    loading: boolean
    isEarningsTableIncomplete : boolean
    isEarningsTableValid: boolean,
    dynamicErrorMsg: string, // display this when `isEarningsTableValid` is false
    lastFYStr: string,
    thisFYStr: string,
}

const C_ERR_MSG_REGIONS_RULE = 'The regional market can only be selected if the Earnings for all regional markets are 20% or less OR at least three countries have had earnings recorded.'
const C_ERR_MSG_NONZERO_RULE = 'Earnings amount must be greater than 0.'
const C_ERR_MSG_MANDATORY_RULE = 'The table is mandatory.'
const C_ERR_MSG_MARKETCODES_TIER2_RULE = 'The export markets you selected do not match your existing export markets that you selected in Question 2.'
const C_ERR_MSG_MARKETCODES_TIER3_RULE = 'The export markets you selected cannot match your new key export markets that you selected in Question 2.'

export class ExportMarketFinancials extends React.Component<Props, State> implements ISupportSpinner {
  ajax: ApplicationController
  editable: ApplicationForm2Editable
  refDisplayErrorTableIncomplete: HTMLDivElement
  refDisplayErrorTableInvalid: HTMLDivElement
  // mandatoryExportEarningError : HTMLDivElement
  timerHandle :NodeJS.Timeout

  constructor(props: Props) {
    super(props)

    this.ajax = new ApplicationController()
    const currDate = new Date()

    this.state = {
      filteredMarkets: this.props.markets.filter((item: Option<string>) => item.label !== 'Worldwide'),
      loading: true,
      isEarningsTableIncomplete: false, // Default to Complete (hide initial Error Msg, only show on Save+Next)
      isEarningsTableValid: true, // Default to Valid (hide initial Error Msg, only show on Save+Next)
      dynamicErrorMsg: '',
      lastFYStr: getLastFY(currDate), // e.g. "2021-22",
      thisFYStr: getThisFY(currDate),
    }
  }

  componentDidMount() {
    const { model, tierNo, onChange } = this.props

    if (model.exportMarketFinancials.length > 0) {
      const newArr = model.exportMarketFinancials.map((item) => {
        if (item.crmExportMarketID === '00000000-0000-0000-0000-000000000000') {
          delete item.crmExportMarketID // remove prop from object, rather than have 000...etc
        }
        if (item.earnings === 0 && tierNo !== 2) {
          delete item.earnings
        }
        return item
      })
      onChange({ exportMarketFinancials: newArr })
    }

    // ajax call moved to parent,simulate ajax call completed
    this.timerHandle = setTimeout(() => {
      this.setState({ loading: false })
      this.timerHandle = null
    }, 200)
  }

  componentWillUnmount() {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle)
      this.timerHandle = null
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { model, onChange } = this.props

    if (prevProps.model.isExportIncomeForLastFY !== model.isExportIncomeForLastFY) {
      // i) WE NEED TO ITERATE to the Existing table, and inject a different FinYear-GUID;
      const newArr = model.exportMarketFinancials.map((row) => {
        // update FinYearGuid on RB-change (IsExportIncomeForLastFY)
        row.crmFinancialYearID = model.financialYearOfExportEarnings
        return row
      })
      // ii)THEN push state up to PARENT !!
      onChange({ exportMarketFinancials: newArr })
    }

    // if-clause to protect against infinite loop!
    if (JSON.stringify(prevProps.model.exportMarketFinancials) !== JSON.stringify(model.exportMarketFinancials)) {
      // Validate everytime table changes;.
      this.localValidate() // no callback
    }
  }

  validateTable = (): boolean => {
    const { tierNo } = this.props
    const isTblIncomplete = this.checkIsEarningsTableIncomplete()
    if (isTblIncomplete) {
      this.setState({
        isEarningsTableIncomplete: true,
      }, () => {
        this.setState({ isEarningsTableValid: true }) // hide other Error Alert
        this.refDisplayErrorTableIncomplete.focus()
      })
      return false
    }

    // any rows are complete; empty table is handled elsewhere.
    this.setState({
      isEarningsTableIncomplete: false,
    })

    // validate other Business Rules: Regional% vs Total
    const isRegionRuleValid = this.checkIsRegionalRuleValid()
    const isAllRowsNonzero = this.checkAllTableRowsAreNonZero()

    if (!isRegionRuleValid) {
      this.setState({
        isEarningsTableValid: false,
        dynamicErrorMsg: C_ERR_MSG_REGIONS_RULE,
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      return false
    } if (!isAllRowsNonzero) {
      this.setState({
        isEarningsTableValid: false,
        dynamicErrorMsg: C_ERR_MSG_NONZERO_RULE,
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      return false
    }

    // validate selected market codes
    const areSelectedMarketCodesValid = this.checkMarketCodesRuleValid()
    if (!areSelectedMarketCodesValid) {
      this.setState({
        isEarningsTableValid: false,
        dynamicErrorMsg: tierNo === 2 ? C_ERR_MSG_MARKETCODES_TIER2_RULE
          : tierNo === 3 ? C_ERR_MSG_MARKETCODES_TIER3_RULE
            : '',
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      return false
    }

    // all validations pass
    this.setState({ isEarningsTableValid: true })
    return true
  }

  localValidate = ():void => {
    this.validateTable()
  }

  lastCheckIsTableValid = (): boolean => {
    const result = this.validateTable()
    return result
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    const { model } = this.props
    const { isEarningsTableIncomplete, isEarningsTableValid } = this.state

    // special case: Mandatory check only on Save+Next
    if (!(model.exportMarketFinancials && model.exportMarketFinancials.length > 0)) {
      this.setState({
        isEarningsTableValid: false,
        dynamicErrorMsg: C_ERR_MSG_MANDATORY_RULE,
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      callback && callback(false) // validation fails
      return
    }

    // Other cases: just prevent Save+Next to continue; error is already on screen.
    if (isEarningsTableIncomplete || !isEarningsTableValid) {
      callback && callback(false)
      return
    }

    /* special scenario: Error is NOT on screen, but we must still re-calc the state AT THE LAST MINUTE, just prior to continuing;
        /*This is because even if the error is already on screen, but user clicked away to another tab(1,2) or on the popup (Add/Edit of PlannedExpenditure section)
        , we would be reloading the Tab3 again WITHOUT an error displayed
        */
    if (!this.lastCheckIsTableValid()) {
      callback && callback(false)
      return
    }

    callback && callback(true)
  }

  // For Calc table totals
  toNumber = (anything: any) => {
    const tempNumber = Number(anything)
    return isNaN(tempNumber) ? 0 : tempNumber
  }

  calcTableTotal = (tableAsArray: ApplicationExportMarketFinancialsModel[]) => {
    const total = tableAsArray.reduce((accum, rowItem) => {
      const updatedSum = this.toNumber(rowItem.earnings) + accum
      return updatedSum
    }, 0)
    return total
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  getInitData(): { columns: Columns[], data: any, skipReset: boolean, model: ApplicationModel, modelPath: string, propertyName: string} {
    const { model } = this.props
    // modelPath: if the Tbl data was nested in the model, we'd use 'Prop1.Prop2.PropTblname'
    const tempVal = {
      ...initialDataExportMarketFinancialConfig(this.state.filteredMarkets), data: model.exportMarketFinancials, model, modelPath: 'exportMarketFinancials', propertyName: 'exportMarketFinancials',
    }
    return tempVal
  }

  // fires whenever new row is added.
  setTableIsEmptyFlagToFalse = () => {
    this.setState({
      isEarningsTableIncomplete: false,
    })
  }

  checkIsEarningsTableIncomplete(): boolean {
    const { model } = this.props

    if (model.exportMarketFinancials.length > 0) {
      const fullyCompleteRowsArr = model.exportMarketFinancials.filter(
        (rowObj) => {
          if (Object.keys(rowObj).length === 0) {
            return false
          } if (
            (!(rowObj.hasOwnProperty('crmExportMarketID'))
                            || (!(rowObj.hasOwnProperty('earnings'))))
                        || ((rowObj.crmExportMarketID === undefined)
                            || (rowObj.earnings === undefined))
                        || (rowObj.earnings === null)
                        || (rowObj.earnings.toString() === '')
          ) {
            return false
          }
          return true // i.e. this return is for the Array.filter;
        },
      )

      if (fullyCompleteRowsArr.length === model.exportMarketFinancials.length) return false // table is good, i.e. incomplete is false;
      return true // table is bad, i.e. incomplete is true;
    }
    // else Table is EMPTY (validated on Save+Next only).
  }

  checkIsRegionalRuleValid() {
    const { model, marketsRegions } = this.props

    // Get Tbl rows which are regions
    const earningRowsWhichAreRegions = model.exportMarketFinancials.filter(
      (rowObj) => {
        if (marketsRegions.includes(rowObj.crmExportMarketID)) return true
        return false
      },
    )
    if (earningRowsWhichAreRegions.length == 0) return true // table is Valid, NO regions are used.

    // find CountryOnlyRows.
    const earningRowsCountryOnly = model.exportMarketFinancials.filter(
      (rowObj) => {
        if (marketsRegions.includes(rowObj.crmExportMarketID)) return false
        return true
      },
    )

    const percentage = this.calcEarningsForRegionsAsPercentOfTotal(earningRowsWhichAreRegions)
    if (percentage <= 20) {
      return true
    } if ((percentage > 20) && (earningRowsCountryOnly.length < 3)) {
      // rule is: must use AT LEAST 3 countries, if % is greater than 20
      return false
    }
    return true // if didn't fail above, it must be true.
  }

  checkAllTableRowsAreNonZero() {
    const { model, tierNo } = this.props
    let result = true

    const zeroArr = model.exportMarketFinancials.filter((row) => row.earnings <= 0)

    if (tierNo === 2) {
      if (zeroArr.length === model.exportMarketFinancials.length) { return false }
    } else if (zeroArr.length > 0) { result = false }

    return result
  }

  checkMarketCodesRuleValid() {
    const { model, tierNo } = this.props

    // For Tier 2, must enter financials for all current export markets
    if (tierNo === 2) {
      const hasFinancialsForAllCurrentExportMarkets = model.marketsCodes.every((marketCode) => model.exportMarketFinancials.some((e) => e.crmExportMarketID === marketCode))
      return hasFinancialsForAllCurrentExportMarkets
    }

    // For Tier 3, market selected in export financials should NOT match current export markets
    if (tierNo === 3) {
      const areAllNewMarkets = model.exportMarketFinancials.every((t) => !model.marketsCodes.includes(t.crmExportMarketID))
      return areAllNewMarkets
    }

    return true
  }

  calcEarningsForRegionsAsPercentOfTotal(earningRowsWhichAreRegions: ApplicationExportMarketFinancialsModel[]) {
    const { model } = this.props

    const totalEarnings = model.exportMarketFinancials.reduce(
      (cumm, rowObj) => cumm + this.toNumber(rowObj.earnings),
      0,
    )

    const regionalEarnings = earningRowsWhichAreRegions.reduce(
      (cumm, rowObj) => cumm + this.toNumber(rowObj.earnings),
      0,
    )

    return (regionalEarnings / totalEarnings) * 100
  }

  getSelectedCategories(): string[] {
    const { model: { exportMarketFinancials } } = this.props
    const tempVal = exportMarketFinancials.map((f) => f.crmExportMarketID)
    return tempVal
  }

  localOnChange = (changes: Partial<ApplicationModel>, callback?: { (): any }) => {
    const { onChange, model } = this.props
    const CRM_MarketInterestID = 498650001 // for both tier-2,tier-3;

    let totExportEarnings = 0
    // if (changes.hasOwnProperty('exportMarketFinancials')) {
    if (changes.exportMarketFinancials.length > 0) { totExportEarnings = this.calcTableTotal(changes.exportMarketFinancials) } else { totExportEarnings = 0 } // table is Empty

    // inject missing fields for DB
    const newArr = changes.exportMarketFinancials.map((row) => {
      row.exportMarketFinancialID = 0 // SET ALL PK to 0, as they will be ADDITIONS; prior to saving, existing db recs will be deleted;
      row.applicationID = model.applicationID // the FK;
      row.crmMarketInterestID = CRM_MarketInterestID
      row.crmFinancialYearID = model.financialYearOfExportEarnings // this.state.lastFYGuid;
      // if not set, 2 fields are set automatically to their default values when sent for saving in the DB
      // row.crmExportMarketID = "00000000-0000-0000-0000-000000000000"; row.earnings = 0.
      return row
    })
    // refresh updated UI-table, including totals, to the `model` in the parent component;
    onChange({ exportMarketFinancials: newArr, exportEarningsForFY: totExportEarnings })
    // then componentDidUpdate of this child should run, because parent state has updated!
  }

  render() {
    const { preview, model, tierNo } = this.props
    const {
      loading, isEarningsTableIncomplete, isEarningsTableValid, lastFYStr, thisFYStr,
    } = this.state

    /*
        if (preview) { //NB: whenever preview is true, loading is also true!
            return <h4>debug, render this</h4>
        }
        */

    if (loading && !preview) {
      return null
    } // else if preview === true, continue to render, regardless of loading!

    let questionNo
    if (tierNo === 2) {
      questionNo = '32.b'
    } else if (tierNo === 3) {
      questionNo = '35.b'
    }

    const lastFYQuestionStr = `Provide details of your export income by market for the last (${lastFYStr}) financial year.`
    const thisFYQuestionStr = `Provide details of your export income by market for this (${thisFYStr}) financial year.`
    const thisOrLastFY = model.isExportIncomeForLastFY
      ? lastFYQuestionStr
      : thisFYQuestionStr

    return (
      <>

        <Label
          label={`${questionNo} ${thisOrLastFY} *`}
          preview={preview}
          description={(
            <i>
              By order of value (highest earning market first), list the markets that your business is currently exporting to, up to a maximum of 10 markets.
              {' '}
              If you have no export income for the market in the last financial year, you can enter zero.
            </i>
          )}
        />

        {tierNo === 2
          && (
          <Alert className="emdg-page-alerts">
            Your existing export markets listed here must include all your existing export markets that you selected in Question 2 and any additional markets which you have exported to.
          </Alert>
          )}

        {tierNo === 3
          && (
          <Alert className="emdg-page-alerts">
            Your selected markets here must be markets that you have previously exported to. These export markets must not match your new key export markets that you selected in Question 2.
          </Alert>
          )}

        <ApplicationForm2Editable
          ref={(r) => { this.editable = r }}
          onChange={this.localOnChange}
          initData={this.getInitData()}
          uniqueSelectSelection
          selectedCategories={this.getSelectedCategories()}
          updateIsTableEmptyToFalseHandler={this.setTableIsEmptyFlagToFalse}
          maximumRows={10}
        />

        {isEarningsTableIncomplete && (
        <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableIncomplete = r }} className="mt-2">
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            The table has incomplete details.
          </Alert>
        </div>
        )}

        {!isEarningsTableValid && (
        <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableInvalid = r }} className="mt-2">
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            {this.state.dynamicErrorMsg}
          </Alert>
        </div>
        )}

      </>
    )
  }
}
